import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { ArrowLeft, Mail, Settings as SettingsIcon } from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";

import {
  PageHeader,
  Card,
  Switch,
  NumberInput,
  TagInput,
  FormField,
  Button,
  Spinner,
} from "../../design-system";

class ContactSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      settings: {
        enabled: true,
        ccEmails: [],
        recaptchaEnabled: true,
        recaptchaMinScore: 0.5,
      },
      loading: true,
      saving: false,
    };
  }

  componentDidMount() {
    this.loadSettings();
  }

  loadSettings = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get("/api/contacts/settings");
      this.setState({
        settings: response.data.settings || this.state.settings,
        loading: false,
      });
    } catch (error) {
      console.error("Error loading settings:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur", message: "Impossible de charger les paramètres",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ saving: true });
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.put("/api/contacts/settings", this.state.settings);
      Store.addNotification({
        title: "Paramètres enregistrés",
        type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      this.setState({ settings: response.data.settings, saving: false });
    } catch (error) {
      console.error("Error saving settings:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Erreur lors de la sauvegarde",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
      this.setState({ saving: false });
    }
  };

  update = (patch) => this.setState({ settings: { ...this.state.settings, ...patch } });

  render() {
    const { settings, loading, saving } = this.state;

    if (loading) {
      return (
        <AdminPageShell pageTitle="Paramètres contacts">
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    return (
      <AdminPageShell pageTitle="Paramètres contacts">
        <PageHeader
          eyebrow="— Contacts"
          title="Paramètres"
          description="Configure l'envoi d'email, la protection reCAPTCHA et les destinataires en CC."
          actions={
            <Button
              variant="secondary"
              leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
              onClick={() => this.props.history.push("/dashboard/contacts")}
            >
              Retour
            </Button>
          }
        />

        <form onSubmit={this.handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              <Card>
                <Card.Header>
                  <Card.Title className="flex items-center gap-2">
                    <SettingsIcon className="w-4 h-4" strokeWidth={1.8} />Configuration générale
                  </Card.Title>
                </Card.Header>
                <div className="space-y-5">
                  <Switch
                    checked={settings.enabled}
                    onChange={(e) => this.update({ enabled: e.target.checked })}
                    label="Activer l'envoi d'email"
                    description="Si désactivé, les contacts seront enregistrés mais aucun email ne sera envoyé."
                  />
                  <Switch
                    checked={settings.recaptchaEnabled}
                    onChange={(e) => this.update({ recaptchaEnabled: e.target.checked })}
                    label="Activer reCAPTCHA v3"
                    description="Protection anti-bot. Score 0.0 (bot) à 1.0 (humain)."
                  />
                  {settings.recaptchaEnabled && (
                    <FormField
                      label="Score minimum reCAPTCHA"
                      helper="0.5 est un bon compromis. En dessous, les messages sont marqués « bloqués »."
                    >
                      <NumberInput
                        value={settings.recaptchaMinScore}
                        onChange={(value) => this.update({ recaptchaMinScore: value })}
                        min={0}
                        max={1}
                        step={0.1}
                      />
                    </FormField>
                  )}
                </div>
              </Card>
            </div>

            <div className="space-y-5">
              <Card>
                <Card.Header>
                  <Card.Title className="flex items-center gap-2">
                    <Mail className="w-4 h-4" strokeWidth={1.8} />Emails en CC
                  </Card.Title>
                  <Card.Description>Ces adresses recevront une copie de tous les messages.</Card.Description>
                </Card.Header>
                <FormField label="Adresses">
                  <TagInput
                    value={settings.ccEmails || []}
                    onChange={(ccEmails) => this.update({ ccEmails })}
                    placeholder="vous@exemple.fr"
                  />
                </FormField>
              </Card>

              <Card variant="filled">
                <div className="space-y-2">
                  <Button type="submit" fullWidth isLoading={saving}>Enregistrer</Button>
                  <Button
                    type="button"
                    variant="ghost"
                    fullWidth
                    onClick={() => this.props.history.push("/dashboard/contacts")}
                  >
                    Annuler
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </AdminPageShell>
    );
  }
}

export default withRouter(ContactSettings);
